import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuantityValidation } from './useCartQuantityValidation';

import { CartItem, QuantityInputOptions } from '../types/CartTypes';

export const useQuantityInput = (
  cartItems: CartItem[],
  options: QuantityInputOptions,
) => {
  const { parseQuantityInput } = useQuantityValidation();
  const {
    onQuantityChange,
    onValidationError = alert,
    debounceDelay = 1000,
  } = options;

  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const debounceRefs = useRef<Record<string, NodeJS.Timeout>>({});

  const handleInputChange = useCallback(
    (uuid: string, inputValue: string) => {
      const item = cartItems.find((items) => items.uuid === uuid);
      if (!item) return;

      setInputValues((prev) => ({
        ...prev,
        [uuid]: inputValue,
      }));

      if (debounceRefs.current[uuid]) {
        clearTimeout(debounceRefs.current[uuid]);
      }

      debounceRefs.current[uuid] = setTimeout(() => {
        const validation = parseQuantityInput(item, inputValue);

        if (!validation.isValid && validation.message) {
          onValidationError(validation.message);
        }

        const finalQuantity = validation.validQuantity;

        setInputValues((prev) => ({
          ...prev,
          [uuid]: finalQuantity.toString(),
        }));

        onQuantityChange(uuid, finalQuantity);
      }, debounceDelay);
    },
    [
      cartItems,
      parseQuantityInput,
      onQuantityChange,
      onValidationError,
      debounceDelay,
    ],
  );

  const getInputValue = useCallback(
    (uuid: string, currentQuantity: number): string => {
      return inputValues[uuid] ?? currentQuantity.toString();
    },
    [inputValues],
  );

  const resetInputValue = useCallback((uuid: string, quantity: number) => {
    setInputValues((prev) => ({
      ...prev,
      [uuid]: quantity.toString(),
    }));
  }, []);

  useEffect(() => {
    const timeouts = debounceRefs.current;

    return () => {
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, []);

  return {
    handleInputChange,
    getInputValue,
    resetInputValue,
  };
};
